import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api/ApplicationApi';
import { BaseDialog } from 'library/components/controls/dialogs/BaseDialog';
import {
	InlineDisplayTextContainer,
	InlineDisplayValue,
} from 'library/components/controls/display/InlineDisplayValue';

import { ServiceContract } from './types';

export type TerminateServiceContractDialogProps = {
	serviceContract: ServiceContract;
	onClose: () => void;
	onConfirm: () => void;
};

export const TerminateServiceContractDialog = ({
	serviceContract,
	onClose,
	onConfirm,
}: TerminateServiceContractDialogProps) => {
	const { api } = useProductCenterApi();
	const { t } = useTranslation(['serviceContracts', 'commonButton']);
	const { enqueueSnackbar } = useSnackbar();

	const handleOnConfirm = useCallback(async () => {
		if (!serviceContract.id) {
			enqueueSnackbar(
				t('serviceContracts:status-change-failure-snackbar'),
				{
					variant: 'error',
				}
			);
			return;
		}

		try {
			await api.changeServiceContractStatus(
				serviceContract.id,
				'Terminated'
			);
		} catch {
			enqueueSnackbar(
				t('serviceContracts:status-change-failure-snackbar'),
				{
					variant: 'error',
				}
			);
			return;
		}

		enqueueSnackbar(t('serviceContracts:terminate-success-snackbar'));
		onConfirm();
		onClose();
	}, [api, enqueueSnackbar, serviceContract.id, onConfirm, onClose, t]);

	return (
		<BaseDialog
			title={t('serviceContracts:terminate-service-contract-title')}
			onClose={onClose}
			onConfirm={handleOnConfirm}
			confirmButtonText={t('commonButton:yes')}
			closeButtonText={t('commonButton:no')}>
			<Typography sx={{ mt: 2, mb: 4 }}>
				{t('serviceContracts:terminate-service-contract-description')}
			</Typography>
			<InlineDisplayTextContainer columnsCount={1} rowsCount={2}>
				<InlineDisplayValue
					label={t('serviceContracts:service-contract')}
					value={serviceContract.id}
				/>
				<InlineDisplayValue
					label={t('serviceContracts:status')}
					value={serviceContract.status}
				/>
			</InlineDisplayTextContainer>
		</BaseDialog>
	);
};
